"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
  IconButton,
  HStack,
  Text,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { Info } from "lucide-react";
import { IoKeypadOutline } from "react-icons/io5";

interface PressDigitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const PressDigitModal: React.FC<PressDigitModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [delayMs, setDelayMs] = useState<number>(1000);
  const [nameError, setNameError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "press_digit");
      setDescription(
        initialValues.description ||
          "Navigate to the human agent of sales department"
      );
      setDelayMs(initialValues.delay_ms ?? 1000);
    } else {
      setName("press_digit");
      setDescription("Navigate to the human agent of sales department");
      setDelayMs(1000);
    }
  }, [initialValues]);

  const validateToolName = (value: string) => {
    if (!value.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (value.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(value)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSave({
      type: "press_digit",
      name,
      description,
      delay_ms: delayMs,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <IoKeypadOutline size={20} />
            <Text color="secondary.800">Press Digit (IVR Navigation)</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color="secondary.700">Name</FormLabel>
              <Input
                id="press-digit-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter function name"
                focusBorderColor="primary.500"
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color="secondary.700">
                Description (Optional)
              </FormLabel>
              <Textarea
                id="press-digit-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                rows={3}
                focusBorderColor="primary.500"
              />
            </FormControl>

            <FormControl>
              <HStack align="center" justify="space-between">
                <HStack spacing={2}>
                  <FormLabel mb={0} color="secondary.700">
                    Pause Detection Delay (ms)
                  </FormLabel>
                  <Tooltip label="Adds extra wait time after pauses to prevent pressing too early. Ensures digits are pressed only after the IVR fully finishes. Default: 1000ms">
                    <IconButton
                      aria-label="Pause detection delay info"
                      icon={<Info size={14} />}
                      size="xs"
                      variant="ghost"
                    />
                  </Tooltip>
                </HStack>
              </HStack>

              <NumberInput
                id="press-digit-function-delay"
                value={delayMs}
                onChange={(_, valueNumber) =>
                  setDelayMs(Math.min(Math.max(valueNumber ?? 0, 0), 5000))
                }
                min={0}
                max={5000}
                step={100}
              >
                <NumberInputField placeholder="Enter delay in milliseconds" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>

              <Text fontSize="sm" color="gray.600" mt={1}>
                Valid range: 0–5000 ms
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.200">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="primary.500"
            color="white"
            _hover={{ bg: "primary.600" }}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
